"use strict";
/**
 * Polymarket US Order Creation (Client Order Intent Payload)
 * Sends minimal intent details to the backend for order construction.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.createPolymarketUsOrderRequest = createPolymarketUsOrderRequest;
const state_1 = require("../state");
const sport_mode_1 = require("../ui/sport-mode");
const slippage_utils_1 = require("./slippage-utils");
const creation_1 = require("./creation");
const permissions_1 = require("../permissions");
const displayed_ask_history_1 = require("./displayed-ask-history");
/**
 * Build a minimal PM-US order intent payload for the backend to execute.
 * Supports Manual Price Mode: passes manualLimitPrice when active.
 * Supports combined baseball + manual price mode: when options.manualPrice is
 * provided (per-button price), uses it directly. When baseballMode is active
 * without a per-button price (MISC/EXT buttons), uses slippage instead of the
 * global manual price. This mirrors the behaviour of createOrderRequest for Kalshi.
 */
function createPolymarketUsOrderRequest(marketSlug, side, action = "buy", options = {}) {
    const appState = (0, state_1.getState)();
    const currentMarket = appState.selectedMarket;
    if (!currentMarket) {
        throw new Error("No market selected");
    }
    const position = appState.currentPosition;
    const yesQuantity = position?.yes_quantity || 0;
    const noQuantity = position?.no_quantity || 0;
    // Owner-gated (server-enforced too); force off when the tier can't use it.
    const closeOppositeOnBuy = appState.tradingSettings.closeOppositeOnBuy !== false &&
        (0, permissions_1.permsAllowCloseOpposite)(appState.permissions);
    const orderDollarAmount = options.dollarAmount ?? appState.tradingSettings.dollarAmount;
    // Tag fanned-out additional-market orders so the server can gate them
    // (allowAdditionalMarkets). Set only by the additional-market fan-out.
    const additionalMarket = options.additionalMarket === true;
    // Tag fanned-out parallel-market orders so the server can gate them
    // (allowParallelMarkets). Set only by the parallel-market fan-out.
    const parallelMarket = options.parallelMarket === true;
    // The server sizes sells and the close from its own count
    // (ai/POSITION_CACHE_PLAN.md §5a); a leg never closes opposite.
    const closeOpposite = action === "buy" && closeOppositeOnBuy && !parallelMarket && !additionalMarket;
    const sellMode = action === "sell" ? appState.tradingSettings.sellMode : undefined;
    // ============================================================================
    // Per-button baseball manual price override (combined baseball + manual mode)
    // ============================================================================
    if (options.manualPrice !== undefined) {
        const manualLimitPrice = options.manualPrice / 100;
        const basePayload = {
            marketSlug,
            side,
            action,
            slippagePercent: 0,
            closeOpposite,
            ...(sellMode ? { sellMode } : {}),
            manualLimitPrice,
            additionalMarket,
            parallelMarket,
            clickId: options.clickId,
            // MANUAL NOTE: we are neglecting minquantity partial share calculating; not worth the potential micro-seconds
            // useDelayedPrice: appState.tradingSettings.useDelayedPrice === true,
            // ...(currentMarket.minimumTradeQty !== undefined
            // 	? { minimumTradeQty: currentMarket.minimumTradeQty }
            // 	: {}),
        };
        if (action === "sell") {
            const sellQuantity = side === "yes" ? yesQuantity : noQuantity;
            if (sellQuantity <= 0) {
                throw new Error(`No ${side.toUpperCase()} position to sell`);
            }
            const finalSellQty = (0, creation_1.partialSellQuantity)(appState.tradingSettings) ?? sellQuantity;
            return { ...basePayload, sellQuantity: finalSellQty };
        }
        if (orderDollarAmount <= 0) {
            throw new Error("Dollar amount must be greater than 0");
        }
        return { ...basePayload, dollarAmount: orderDollarAmount };
    }
    // ============================================================================
    // Pure manual price mode (no sport layout): validate selection and use the
    // global price. When a sport layout is showing (MISC/EXT buttons, sells),
    // fall through to the slippage path below.
    // ============================================================================
    const isManualMode = appState.tradingSettings.manualPriceMode === true;
    const sportLayoutActive = (0, sport_mode_1.isSportLayoutActive)(appState.tradingSettings);
    if (isManualMode && !sportLayoutActive) {
        const manualPriceSelected = side === "yes"
            ? appState.tradingSettings.manualYesPriceSelected === true
            : appState.tradingSettings.manualNoPriceSelected === true;
        if (!manualPriceSelected) {
            throw new Error(`Select a manual ${side.toUpperCase()} price before placing an order`);
        }
    }
    // Manual price as decimal (e.g., 55 → 0.55) for PM-US, only in pure manual mode
    const manualLimitPrice = isManualMode && !sportLayoutActive
        ? (side === "yes"
            ? appState.tradingSettings.manualYesPrice
            : appState.tradingSettings.manualNoPrice) / 100
        : undefined;
    const effectiveSlippage = options.overrideSlippage !== undefined
        ? options.overrideSlippage
        : isManualMode && !sportLayoutActive
            ? 0
            : (0, slippage_utils_1.getEffectiveSlippage)(appState.tradingSettings, side);
    // The ask actually on screen, and how stale it was when clicked (same clock
    // at both ends, so no skew to correct). Undefined keys drop out of the JSON.
    // The ask displayed ~one reaction time before this click, with its real age.
    // Buys in auto mode only: a sell prices off the bid, and a manual limit is
    // authoritative on its own.
    const seen = action === "buy" && manualLimitPrice === undefined
        ? (0, displayed_ask_history_1.getDisplayedAsk)(marketSlug, side)
        : null;
    const askSeen = seen?.price;
    const askSeenAgeMs = seen?.ageMs;
    const basePayload = {
        marketSlug,
        side,
        action,
        slippagePercent: effectiveSlippage,
        askSeen,
        askSeenAgeMs,
        closeOpposite,
        ...(sellMode ? { sellMode } : {}),
        ...(manualLimitPrice !== undefined ? { manualLimitPrice } : {}),
        // MANUAL NOTE: we are neglecting minquantity partial share calculating; not worth the potential micro-seconds
        // ...(currentMarket.minimumTradeQty !== undefined
        // 	? { minimumTradeQty: currentMarket.minimumTradeQty }
        // 	: {}),
        // Delayed pricing is a trusted-tier feature; force T0 (real-time) off when
        // the tier can't use it so a stale persisted preference never leaks through.
        useDelayedPrice: appState.tradingSettings.useDelayedPrice === true &&
            (0, permissions_1.permsAllowDelayedPrice)(appState.permissions),
        additionalMarket,
        parallelMarket,
        clickId: options.clickId,
    };
    if (action === "sell") {
        const sellQuantity = side === "yes" ? yesQuantity : noQuantity;
        if (sellQuantity <= 0) {
            throw new Error(`No ${side.toUpperCase()} position to sell`);
        }
        const finalSellQty = (0, creation_1.partialSellQuantity)(appState.tradingSettings) ?? sellQuantity;
        return {
            ...basePayload,
            sellQuantity: finalSellQty,
        };
    }
    if (orderDollarAmount <= 0) {
        throw new Error("Dollar amount must be greater than 0");
    }
    return {
        ...basePayload,
        dollarAmount: orderDollarAmount,
    };
}
